import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { FormControl, Validators } from '@angular/forms';
import { filter, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { ErrorHandlingService } from '../../services/error-handling.service';
import { ToggleService } from '../../shared/toggle-service/toggle.service';
import { UserService } from '../../services/user.service';
import { AccountService } from '../../services/account.service';
import { TitleService } from '../../services/title.service';
import { AutomapService } from '../../services/automap.service';
import { DownloadService } from '../../services/download.service';
import { NotificationService } from '../../services/notification.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../../../environments/environment';
import { QueryParamService } from '../../services/query-param.service';

@Component({
  standalone: false,
  selector: 'app-automap',
  templateUrl: './automap.component.html',
  styleUrls: ['./automap.component.css'],
})
export class AutomapComponent implements OnInit {
  automapSubscription: Subscription;
  taskSubscription: Subscription;
  auditSubscription: Subscription;
  postTaskSubscription: Subscription;
  patchTaskSubscription: Subscription;
  postMappingSubscription: Subscription;
  postSearchMapSubscription: Subscription;
  provisionalSubscription: Subscription;
  apiLink: string;
  metadata: any;
  entityConfig: any = [];
  sortField = 'modified';
  sortAscending = false;
  columnSort = {
    name: true,
    modified: false,
  };
  perPage = 5;
  page = 1;
  perPageValue: any;
  totalPages: any;
  totalTerms: any;
  searchValue = '';
  searchMapValue = '';
  terminologiesList: any = [];
  allTerms: any = [];
  currentTerms: any = [];
  finalIndex: any;
  taskDetails: any = [];
  auditDetails: any = [];
  modalDisplay = '';
  termSelected = false;
  selectedTerm: any = [];
  allMappings: any = [];
  activeMappings: any = [];
  currentActiveMappings: any = [];
  mapping = null;
  mappingField = 'confidence';
  mappingAscending = false;
  mappingPage = 1;
  mappingPerPage = 100;
  totalPagesMap: any;
  perPageValMap: any;
  finalIndexMap: any;
  allMappingsGrouped = false;
  mappingCol = {
    name: true,
    modified: true,
  };
  selectedEntityType: { label: string; value: string };
  entityTypeOptions = [];
  notSubmitted = true;
  filters = [
    { label: 'no maps', value: 'nomaps', selected: false },
    { label: 'provisional', value: 'provisional', selected: false },
  ];
  instructions = 'Double-click one or more rows to select correct mapping entries for this term.';
  editInstructions = 'Click Edit Mapping to change status and update mapping entries for this term.';
  selectedMappings: any = [];
  modalErrMessage = 'Error';
  tableDownloading = false;
  downloadSearchMessage: string;
  sortStatus: { label: string; value: string };
  sortStatusOptions = [];
  statusSort = '*';
  sortEntityType: { label: string; value: string };
  sortEntityTypeOptions = [];
  entityTypeSort = '*';
  showEntityConfigTable = true;
  modalTitle = '';
  modalContent = '';
  mapGroup = '';
  isAdmin: boolean;
  removeId = '';
  newConfidence = new FormControl('0.8', [Validators.required]);
  newTerm = new FormControl('', [Validators.required]);
  newTerminology = new FormControl('');
  newCode = new FormControl('');
  searchFC = new FormControl('');
  searchMap = new FormControl('');
  dropdowns = [
    { title: '', label: '' },
    { title: '', label: '' },
  ];
  isFhirTerm = false;
  fhirTermError = '';

  @ViewChild('taskModal') taskModal: any;
  @ViewChild('auditModal') auditModal: any;
  @ViewChild('newTaskModal') newTaskModal: any;
  @ViewChild('removeTermModal') removeTermModal: any;
  @ViewChild('mapErrModal') mapErrModal: any;
  @ViewChild('jsonModal') jsonModal: any;
  @ViewChild('metadataModal') metadataModal: any;
  @ViewChild('metadataTypeModal') metadataTypeModal: any;
  @ViewChild('DownloadModal') downloadModal: any;
  @ViewChild('termsTable') termsTable!: ElementRef;
  @ViewChild('mappingsTable') mappingsTable!: ElementRef;
  @ViewChild('entityConfigTable') confTable!: ElementRef;

  constructor(
    readonly toggleService: ToggleService,
    private ngxLoader: NgxUiLoaderService,
    private errorHandlingService: ErrorHandlingService,
    private router: Router,
    private route: ActivatedRoute,
    private titleService: TitleService,
    readonly userService: UserService,
    readonly accountService: AccountService,
    private automapService: AutomapService,
    private readonly downloadService: DownloadService,
    private readonly modalService: NgbModal,
    private notificationService: NotificationService,
    private queryParams: QueryParamService,
  ) {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.setTabTitle();
    });

    this.searchFC.valueChanges.pipe(debounceTime(600), distinctUntilChanged()).subscribe((res) => {
      this.search();
    });

    this.setNewTermDebounce();
  }

  ngOnInit(): void {
    this.setTabTitle();
    setTimeout(() => {
      this.isAdmin = JSON.parse(localStorage.getItem('user_data_automap'))?.role?.toLowerCase() == 'admin';
    }, 1000);
    this.apiLink = `${environment.url}/swagger-ui/index.html`;
    this.finalIndex = this.perPageValue = 5;
    this.finalIndexMap = this.perPageValMap = 100;
    this.selectedEntityType = { label: 'condition', value: 'condition' };
    this.sortStatus = { label: 'All', value: '*' };
    this.sortEntityType = { label: 'All', value: '*' };
    this.sortEntityTypeOptions = JSON.parse(JSON.stringify(this.entityTypeOptions));
    this.sortEntityTypeOptions.unshift(this.sortEntityType);
    this.dropdowns = [
      { title: 'Entity Type', label: 'All' },
      { title: 'Status', label: 'All' },
    ];
    this.getMetadata();
    this.getEntityConfig();
    this.getTerminologiesList();
    this.queryParamsForSearch();
    this.loadTerms();
  }

  setTabTitle() {
    this.titleService.setTitle(this.route.snapshot, this.router.routerState.snapshot);
  }

  getMetadata() {
    this.automapService.getMetadata().subscribe(
      (data) => {
        this.metadata = data;
        let defaultFound = false;
        for (const option of Object.keys(this.metadata['term.entityType'])) {
          if (option.toLowerCase() === 'condition') {
            defaultFound = true;
          }
          this.entityTypeOptions.push({ label: option, value: option });
        }
        this.entityTypeOptions = this.entityTypeOptions.sort((a, b) => a.label.localeCompare(b.label));
        if (!defaultFound) {
          if (this.entityTypeOptions.length > 0) {
            this.selectedEntityType = this.entityTypeOptions[0];
          }
        }
        this.sortEntityTypeOptions = JSON.parse(JSON.stringify(this.entityTypeOptions));
        this.sortEntityTypeOptions.unshift(this.sortEntityType);
        for (const state of Object.keys(this.metadata['term.status'])) {
          this.sortStatusOptions.push({ label: state.replaceAll('_', ' '), value: state });
        }
        this.sortStatusOptions.unshift(this.sortStatus);
      },
      (err) => {
        this.errorHandlingService.handleError(err, err.url);
      },
    );
  }

  getEntityConfig() {
    this.automapService.getEntityConfig().subscribe(
      (data) => {
        this.entityConfig = data;
      },
      (err) => {
        this.errorHandlingService.handleError(err, err.url);
      },
    );
  }

  getTerminologiesList() {
    this.automapService.getTerminologiesList().subscribe(
      (data) => {
        this.terminologiesList = data.items;
      },
      (err) => {
        this.errorHandlingService.handleError(err, err.url);
      },
    );
  }

  getTerminologyName(value: string) {
    for (const terminology of this.terminologiesList) {
      if (terminology.value === value) {
        return terminology.key;
      }
    }
  }

  loadTerms(): any {
    if (this.automapSubscription) {
      this.automapSubscription.unsubscribe();
    }
    const page = this.page - 1;
    const offset = page * this.perPage;
    this.ngxLoader.start();
    let query = '';
    if (this.searchValue !== query) {
      query = this.searchValue.trim().includes(':')
        ? this.searchValue
        : this.searchValue
            .split(' ')
            .map(function (s) {
              return s.endsWith('*') ? s : s.concat('*');
            })
            .join(' AND ');
    }

    let fullQuery = query ? query : '';
    if (this.statusSort !== '*' || this.entityTypeSort !== '*') {
      let sortQueries = '';
      if (this.statusSort !== '*' && this.entityTypeSort !== '*') {
        sortQueries = `status:${this.statusSort.toUpperCase()} AND entityType:${this.entityTypeSort.toUpperCase()}`;
      } else {
        if (this.statusSort !== '*') {
          sortQueries = `status:${this.statusSort.toUpperCase()}`;
        }
        if (this.entityTypeSort !== '*') {
          sortQueries = `entityType:${this.entityTypeSort.toUpperCase()}`;
        }
      }
      if (fullQuery == '') {
        fullQuery = sortQueries;
      } else {
        fullQuery += ` AND ${sortQueries}`;
      }
    }

    this.automapSubscription = this.automapService.getTerms(fullQuery, offset, this.perPage, this.sortField, this.sortAscending).subscribe(
      (data) => {
        this.totalTerms = data.total;
        this.totalPages = data.total;
        this.allTerms = data?.items ?? [];
        this.updateTermsLink();
        this.currentTerms = data?.items ?? [];
        this.termSelect(this.allTerms[0]);
        this.ngxLoader.stop();
      },
      (err) => {
        this.ngxLoader.stop();
        this.errorHandlingService.handleError(err, 'query error');
      },
    );
  }

  updateTermsLink() {
    this.allTerms.map((member) => (member.searchUrl = `https://www.google.com/search?q=${encodeURIComponent(member.term)}`));
  }

  //In the HTML, there is something that prevents the "search term on the web" button from working on a single click
  //But we need to keep the href property so the user can still right click to open it
  openLink(url, target = '_blank') {
    window.open(url, target);
  }

  getPercentageString(arr: any = []): any {
    let res = 0;
    let total = 0;
    arr.forEach((element) => {
      if (element.confidence >= 1) res++;
      total++;
    });
    return ((res / total) * 100).toString() + '% (' + res.toString() + '/' + total.toString() + ')';
  }

  toggleFilter(value: string) {
    for (const filter of this.filters) {
      if (filter.value === value) {
        filter.selected === true ? (filter.selected = false) : (filter.selected = true);
      }
      if (filter.selected === true) {
        switch (filter.value) {
          case 'nomaps':
            if (this.searchFC.value === '' || this.searchFC.value === 'mappingCt:0') {
              this.searchFC.setValue('mappingCt:0');
            } else {
              this.searchFC.setValue(`${this.searchFC.value} AND mappingCt:0`);
            }

            break;
          case 'provisional':
            this.statusSort = filter.value;
            this.sortStatus = { label: 'provisional', value: 'provisional' };
            break;
        }
      } else {
        switch (filter.value) {
          case 'nomaps':
            if (this.searchFC.value === 'mappingCt:0' || this.searchFC.value === 'mappingCt:0') {
              this.searchFC.setValue('');
            } else {
              this.searchFC.setValue(this.searchFC.value.replace(' AND mappingCt:0', ''));
            }

            break;
          case 'provisional':
            this.statusSort = '*';
            this.sortStatus = { label: 'All', value: '*' };
            break;
        }
      }
    }
    this.loadTerms();
  }

  search() {
    this.searchValue = this.searchFC.value;
    this.page = 1;
    this.loadTerms();
  }

  searchMapBox() {
    this.searchMapValue = this.searchMap.value;
    this.ngxLoader.start();
    this.activeMappings = [];
    this.currentActiveMappings = [];
    this.totalPagesMap = 0;
    if (this.postSearchMapSubscription) {
      this.postSearchMapSubscription.unsubscribe();
    }
    let req = {
      inputType: 'string',
      entityType: this.selectedTerm.entityType,
      term: this.searchMapValue,
    };
    this.postSearchMapSubscription = this.automapService.postSearchMap(req).subscribe(
      (data) => {
        this.allMappings = data.mappings;
        this.totalPagesMap = this.allMappings.length;
        if (this.allMappings) {
          this.sliceMappings();
        } else {
          this.ngxLoader.stop();
        }
      },
      (err) => {
        this.errorHandlingService.handleError(err, err.url);
        this.ngxLoader.stop();
      },
    );
  }

  exportAllProvisionalTerms() {
    if (this.provisionalSubscription) {
      this.provisionalSubscription.unsubscribe();
    }
    this.ngxLoader.start();
    this.provisionalSubscription = this.automapService.exportTerms('provisional').subscribe(
      (data) => {
        this.ngxLoader.stop();
      },
      (err) => {
        this.ngxLoader.stop();
        this.errorHandlingService.handleError(err, err.url);
      },
    );
    this.ngxLoader.stop();
  }

  selectPaginationLimit(event: any) {
    this.perPage = event;
    if (event === '') {
      this.perPageValue = 100;
      this.perPage = this.finalIndex = 100;
    } else {
      this.finalIndex = this.perPageValue = event;
    }
    this.onPageChange(1);
  }

  selectPaginationLimitMap(event: any) {
    this.mappingPerPage = event;
    if (event === '') {
      this.perPageValMap = 'All';
      this.mappingPerPage = this.finalIndexMap = 100000;
    } else {
      this.finalIndexMap = this.perPageValMap = event;
    }
    this.onPageChangeMap(1);
  }

  onPageChange(event: any): void {
    this.page = event;
    this.loadTerms();
  }

  onPageChangeMap(event: any): void {
    this.mappingPage = event;
    if (this.allMappings) {
      this.sliceMappings();
    }
  }

  tableSorter(sortBy: any) {
    this.sortField = sortBy;
    this.sortAscending = !this.columnSort[sortBy];
    this.columnSort[sortBy] = !this.columnSort[sortBy];
    this.page = 1;
    this.loadTerms();
  }

  mappingSorter(sortBy: any) {
    this.mappingField = sortBy;
    this.mappingAscending = !this.mappingCol[sortBy];
    this.mappingCol[sortBy] = !this.mappingCol[sortBy];
    this.mappingPage = 1;
    if (this.allMappings) {
      this.allMappings.sort((x: Object, y: Object) => {
        if (x[this.mappingField] > y[this.mappingField]) {
          return this.mappingAscending ? 1 : -1;
        } else if (x[this.mappingField] < y[this.mappingField]) {
          return this.mappingField ? -1 : 1;
        }
        return 0;
      });

      this.sliceMappings();
    }
  }

  openModal(term: any, type = '', title = ''): void {
    switch (type) {
      case 'audit': //Audit trail
        this.ngxLoader.start();
        if (this.auditSubscription) {
          this.auditSubscription.unsubscribe();
        }
        this.auditSubscription = this.automapService.getAudit(term.id).subscribe(
          (data) => {
            this.auditDetails = data ?? [];
            this.ngxLoader.stop();
          },
          (err) => {
            this.ngxLoader.stop();
            this.errorHandlingService.handleError(err, err.url);
          },
        );
        this.modalService.open(this.auditModal, { ariaLabelledBy: 'modal-title', backdrop: false, modalDialogClass: 'audit-modal-width' });
        break;
      case 'new task': //New Task
        this.modalService.open(this.newTaskModal, { ariaLabelledBy: 'modal-title', backdrop: false, modalDialogClass: 'feedback-modal-width' });
        break;
      case 'remove term': //Remove Term
        this.removeId = term.id;
        this.modalService.open(this.removeTermModal, { ariaLabelledBy: 'modal-title', backdrop: false, modalDialogClass: 'custom-modal-width' });
        setTimeout(() => {
          document.getElementById('removeBtn').focus();
        }, 500);
        break;
      case 'map error': //Mapping Error
        this.modalService.open(this.mapErrModal, { ariaLabelledBy: 'modal-title', backdrop: false, modalDialogClass: 'custom-modal-width' });
        break;
      case 'metadata': //Metadata
        this.modalService.open(this.metadataModal, { ariaLabelledBy: 'modal-title', backdrop: false, modalDialogClass: 'metadata-modal-width' });
        break;
      case 'metadata type': //Metadata Entity Type
        this.modalService.open(this.metadataTypeModal, { ariaLabelledBy: 'modal-title', backdrop: false, modalDialogClass: 'custom-modal-width' });
        break;
      case 'json': //JSON modals
        this.modalTitle = 'JSON for ' + title;
        this.modalContent = JSON.stringify(term, null, 2);
        this.modalService.open(this.jsonModal, { ariaLabelledBy: 'modal-title', backdrop: false, modalDialogClass: 'custom-modal-width' });
        break;
      default: //Tasks
        this.ngxLoader.start();
        if (this.taskSubscription) {
          this.taskSubscription.unsubscribe();
        }
        this.taskSubscription = this.automapService.getTask(term.taskId).subscribe(
          (data) => {
            this.taskDetails = data ?? [];
            this.ngxLoader.stop();
          },
          (err) => {
            this.ngxLoader.stop();
            this.errorHandlingService.handleError(err, err.url);
          },
        );
        this.modalService.open(this.taskModal, { ariaLabelledBy: 'modal-title', backdrop: false, modalDialogClass: 'custom-modal-width' });
    }
  }

  getJSON(data: any): string {
    return JSON.stringify(data, null, 2);
  }

  mapTags(term: any) {
    let res = '';
    if (term?.tags) {
      term?.tags.forEach((tag) => {
        res += `${tag.key}=${tag.value} ; `;
      });
    }
    return res.slice(0, -2);
  }

  displayMappings(term: any) {
    this.instructions = 'Double-click a row to identify the correct mappings for this term.';
    this.allMappings = term?.mappings;
    if (this.allMappings) {
      this.totalPagesMap = this.allMappings.length;
      this.allMappingsGrouped = this.setGroups();
      this.searchMapValue = '';
      this.searchMap = new FormControl(this.searchMapValue);
      this.searchMap.valueChanges.pipe(debounceTime(600), distinctUntilChanged()).subscribe((res) => {
        this.searchMapBox();
      });

      this.sliceMappings();
    } else {
      this.allMappings = [];
      this.activeMappings = [];
      this.currentActiveMappings = [];
      this.totalPagesMap = 0;
    }
    if (!this.termSelected) {
      this.termSelected = true;
    }
  }

  setGroups(): boolean {
    let grouped = false;
    for (let map of this.allMappings) {
      if (map.startIndex > 0) {
        grouped = true;
      } else {
        if (map.term !== undefined) {
          if (map.endIndex - map.startIndex !== map.term.length && map.term !== map.name) {
            grouped = true;
          }
        }
      }
    }
    return grouped;
  }

  showMappingGroup(map): void {
    this.mapping = map;
    if (map.term !== undefined) {
      if (map.startIndex > 0 || map.endIndex - map.startIndex !== map.term.length || map.term.length !== map.name.length) {
        this.mapGroup = this.selectedTerm.term.slice(map.startIndex, map.endIndex);
      }
    }
  }
  clearMappingGroup(): void {
    this.mapping = null;
  }

  sliceMappings(): void {
    this.activeMappings = this.allMappings.slice((this.mappingPage - 1) * this.mappingPerPage, this.mappingPage * this.mappingPerPage);
    this.currentActiveMappings = JSON.parse(JSON.stringify(this.activeMappings));
    if (this.activeMappings.length < 100) {
      this.perPageValMap = 'All';
      this.mappingPerPage = this.finalIndexMap = 100000;
    }
    this.ngxLoader.stop();
  }

  selectOption(select: any, selected: string) {
    this[selected] = select;
  }

  filterEntityType(select: any) {
    this.sortEntityType = select;
    this.dropdowns = [
      { title: 'Entity Type', label: this.sortEntityType.label },
      { title: 'Status', label: this.sortStatus.label },
    ];
    if (select.label) {
      this.entityTypeSort = select.value;
    } else {
      this.entityTypeSort = '*';
    }
    this.loadTerms();
  }

  filterStatus(select: any) {
    this.sortStatus = select;
    this.dropdowns = [
      { title: 'Entity Type', label: this.sortEntityType.label },
      { title: 'Status', label: this.sortStatus.label },
    ];
    if (select.label) {
      this.statusSort = select.value;
    } else {
      this.statusSort = '*';
    }
    for (const label of this.filters) {
      if (this.statusSort === label.value && label.value === 'provisional') {
        label.selected = true;
      } else {
        label.selected = false;
      }
    }
    this.loadTerms();
  }

  keycheck(e: any) {
    const regex = new RegExp(/^([0]{1,2}){1}(\.[0-9]+)?$/);
    const input = e.target.value + e.key;
    if (e.target.value === '0' && e.charCode === 46) {
      return true;
    } else {
      if (regex.test(input)) {
        return true;
      }
    }
    e.preventDefault();
    return false;
  }

  handlePaste($event: any) {
    this.isFhirTerm = this.newTerm.value.trim().startsWith('{');
  }

  submitNew(modal: any): void {
    this.validateNumberInput();
    if (this.newTerm.value === '') {
      this.errorHandlingService.showError('Error: Please enter a term!');
      return;
    }
    if (Object.keys(this.selectedEntityType).length <= 0) {
      this.errorHandlingService.showError('Error: Please give a valid entity type!');
      return;
    }
    if (this.newConfidence.value === null) {
      this.errorHandlingService.showError('Error: Please enter a valid confidence number!');
      return;
    }
    this.termSelected = false;
    this.selectedTerm = [];
    this.fhirTermError = '';
    const trimmedTerm = this.newTerm.value.trim();
    this.isFhirTerm = trimmedTerm.startsWith('{');
    this.ngxLoader.start();
    let apiCall;
    if (this.isFhirTerm) {
      apiCall = this.automapService.postFhirResource(trimmedTerm, this.newConfidence.value);
    } else {
      let request = {
        topAnswer: false,
        minConfidence: this.newConfidence.value,
        audit: true,
        terms: [
          {
            terminology: this.newTerminology.value,
            code: this.newCode.value,
            term: this.newTerm.value,
            entityType: this.selectedEntityType.value,
          },
        ],
      };
      apiCall = this.automapService.postTask(request);
    }

    if (this.postTaskSubscription) {
      this.postTaskSubscription.unsubscribe();
    }
    this.postTaskSubscription = apiCall.subscribe(
      (data) => {
        this.notSubmitted = false;
        this.ngxLoader.stop();
      },
      (err) => {
        this.ngxLoader.stop();
        this.errorHandlingService.handleError(err, err.url);
        if (this.isFhirTerm && err.status >= 500) {
          this.newTerm.setErrors({ incorrect: true });
          this.fhirTermError = 'There seems to be an error with the FHIR resource text. Please double check your input and try again';
        }
      },
    );
  }

  reloadTerms(modal: any): void {
    this.page = 1;
    this.sortField = 'modified';
    this.sortAscending = false;
    this.searchValue = '';
    this.searchFC.setValue('');
    this.statusSort = '*';
    this.entityTypeSort = '*';
    this.sortStatus = { label: 'All', value: '*' };
    this.sortEntityType = { label: 'All', value: '*' };
    for (const label of this.filters) {
      label.selected = false;
    }
    this.resetSubmitTerms();
    this.loadTerms();
    modal.close();
  }

  selectTerm(event: any, term: any): void {
    if (
      event.srcElement.parentElement.id !== 'taskInfo' &&
      event.srcElement.parentElement.id !== 'auditInfo' &&
      event.srcElement.parentElement.parentElement.id !== 'taskInfo' &&
      event.srcElement.parentElement.parentElement.id !== 'auditInfo'
    ) {
      this.termSelect(term);
    }
  }

  termSelect(term: any): void {
    this.selectedTerm = term;
    this.selectedMappings = [];
    this.mappingField = 'confidence';
    this.mappingAscending = false;
    this.mappingPage = 1;
    this.mappingCol = {
      name: true,
      modified: true,
    };
    this.mappingField = '';
    this.displayMappings(this.selectedTerm);
  }

  cancelChange() {
    this.patchStatus(false);
    this.termSelect(this.selectedTerm);
  }

  validateNumberInput(): void {
    let temp = parseFloat(this.newConfidence.value);
    if (Number.isNaN(temp)) {
      this.newConfidence.setValue(null);
    } else {
      if (temp > 1) {
        temp = 1;
      } else if (temp < 0) {
        temp = 0;
      }
      this.newConfidence.setValue(temp.toString());
    }
  }

  closeSubmitModal(modal: any): void {
    this.resetSubmitTerms();
    modal.close('Cancel');
  }

  resetSubmitTerms(): void {
    this.notSubmitted = true;
    delete this.newTerm;
    this.newTerm = new FormControl('', [Validators.required]);
    this.isFhirTerm = false;
    this.fhirTermError = '';
    this.setNewTermDebounce();
    delete this.newTerminology;
    this.newTerminology = new FormControl('');
    delete this.newCode;
    this.newCode = new FormControl('');

    this.selectedEntityType = { label: 'condition', value: 'condition' };

    delete this.newConfidence;
    this.newConfidence = new FormControl('0.8', [Validators.required]);
  }

  removeTermConfirm() {
    this.automapService.deleteTerm(this.removeId).subscribe(
      (data) => {
        this.removeId = '';
        this.notificationService.showSuccess('Term deleted successfully');
        this.loadTerms();
      },
      (err) => {
        this.errorHandlingService.handleError(err, err.url);
      },
    );
  }

  patchStatus(err = false): void {
    this.ngxLoader.start();
    if (this.patchTaskSubscription) {
      this.patchTaskSubscription.unsubscribe();
    }
    let changeStatus = 'provisional';
    if (err) {
      changeStatus = 'out_of_scope';
    } else if (this.selectedTerm?.status !== 'final') {
      changeStatus = 'final';
    }
    this.patchTaskSubscription = this.automapService.patchStatus(this.selectedTerm?.id, changeStatus).subscribe(
      (data) => {
        this.loadTermsAfterUpdate(data);
        this.ngxLoader.stop();
      },
      (err) => {
        this.ngxLoader.stop();
        this.errorHandlingService.handleError(err, err.url);
      },
    );
  }

  selectMapping(map: any) {
    if (map.selected === true) {
      //This case will also create a selected attrib if it doesn't exist
      map.selected = false;
      const index = this.selectedMappings.indexOf(map);
      this.selectedMappings.splice(index, 1);
    } else {
      map.selected = true;
      this.selectedMappings.push(map);
    }
  }

  validateMappings(): void {
    if (this.selectedMappings.length <= 0) {
      //No mappings selected, this should not trigger
      this.modalErrMessage = 'At least one mapping must be chosen to finish.';
      this.openModal('none', 'map error');
    } else {
      let noTarget = false;
      let selMaps = false;
      for (let i in this.selectedMappings) {
        if (this.selectedMappings[i].name === 'No target') {
          noTarget = true;
        } else {
          selMaps = true;
        }
      }
      if (noTarget) {
        if (selMaps) {
          //No target and other mappings are selected
          this.modalErrMessage = 'Mappings with and without a target may not both be selected.';
          this.openModal('none', 'map error');
        } else {
          //Only no target has been selected
          this.patchStatus(true);
          this.selectedMappings = [];
        }
      } else {
        //Terms other than no target have been selected
        this.updateMappings();
        this.selectedMappings = [];
      }
    }
  }

  updateMappings(): void {
    //Create a POST request with selected term and selected mappings
    //Updating a term with out of scope can be done with
    //patchStatus(true)
    this.ngxLoader.start();
    if (this.postMappingSubscription) {
      this.postMappingSubscription.unsubscribe();
    }
    let req = {
      id: this.selectedTerm.id,
      term: this.selectedTerm.term,
      entityType: this.selectedTerm.entityType,
      mappings: [],
    };
    this.selectedMappings.forEach((map) => {
      req.mappings.push({
        terminology: map.terminology,
        code: map.code,
        name: map.name,
        entityType: map.entityType,
      });
    });
    this.postMappingSubscription = this.automapService.postMapping(req).subscribe(
      (data) => {
        this.loadTermsAfterUpdate(data);
        this.ngxLoader.stop();
      },
      (err) => {
        this.errorHandlingService.handleError(err, err.url);
        this.ngxLoader.stop();
      },
    );
  }

  loadTermsAfterUpdate(newTerm: any): void {
    if (this.sortField === 'modified') {
      if (this.columnSort.modified) {
        this.page = this.totalPages;
      } else {
        this.page = 1;
      }
      this.loadTerms();
    } else {
      for (let i in this.allTerms) {
        if (this.allTerms[i] === this.selectedTerm) {
          this.allTerms[i] = newTerm;
        }
      }
      this.updateTermsLink();
    }
    this.selectedTerm = newTerm;
    this.displayMappings(newTerm);
  }

  getConfidence(confidence: any): any {
    if (confidence === 'NaN' || confidence === undefined || confidence === null) {
      return '';
    } else {
      if (typeof Number) {
        return confidence.toFixed(3);
      } else {
        return '';
      }
    }
  }

  openDownloadModal() {
    this.downloadSearchMessage = 'Click to download all results for the current search.';
    this.modalService.open(this.downloadModal, { ariaLabelledBy: 'modal-title' }).result.then(
      (result) => {
        if (result === 'Download') {
          this.tableDownloading = true;
          this.downloadSearchResults();
        } else {
          this.tableDownloading = false;
        }
      },
      (reason) => {
        this.tableDownloading = false;
      },
    );
  }

  downloadSearchResults(): void {
    let query = '';
    if (this.searchValue !== query) {
      query = this.searchValue.trim().includes(':')
        ? this.searchValue
        : this.searchValue
            .split(' ')
            .map(function (s) {
              return s.endsWith('*') ? s : s.concat('*');
            })
            .join(' AND ');
    }

    let fullQuery = query ? query : '';
    let sortQueries = '';
    sortQueries = `status:${this.statusSort.toUpperCase()} AND entityType:${this.entityTypeSort.toUpperCase()}`;
    if (fullQuery == '') {
      fullQuery = sortQueries;
    } else {
      fullQuery += ` AND ${sortQueries}`;
    }
    const filename = 'search-results-automap-terms-' + this.downloadService.yyyymmddhhmmss(new Date()) + '.xls';
    this.automapService.getTermsTableExport(filename, fullQuery, '').subscribe((data) => {
      this.searchResultCompletion();
    });
  }

  searchResultCompletion() {
    const downloadInterval = setInterval(() => {
      if (this.downloadService.downloadCompleted) {
        this.downloadService.downloadCompleted = false;
        this.tableDownloading = false;
        clearInterval(downloadInterval);
      }
    }, 2500);
  }

  setNewTermDebounce() {
    this.newTerm.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((res) => {
      this.isFhirTerm = this.newTerm.value.trim().startsWith('{');
    });
  }

  queryParamsForSearch() {
    const queryFromParams = this.queryParams.getQueryParam('query');
    if (queryFromParams) {
      this.searchValue = decodeURIComponent(queryFromParams);
      this.searchFC.setValue(decodeURIComponent(queryFromParams));
      this.queryParams.removeQueryParam('query');
    }
  }
}
